import { SCREENSHARE_VIDEO_ID } from '../../constants/screenshare';
import { LayoutType, RecordingVideoType } from '../../types/asset-types';
import { BrandKitStore, PlatformBrandKitStore } from '../../types/brand-kit-types';
import { Clip } from '../../types/clip-types';
import { ElementType } from '../../types/Element/Type';
import { VideoElement, SpeakerLabel } from '../../types/RemotionPlayerConfigTypes/Element/VideoElement';
import { Scenes } from '../../types/RemotionPlayerConfigTypes/Scene';
import { SpeakerWithDetails } from '../../types/speaker-types';
import { SpeakersAnalysis } from '../../types/speakers-analysis';
import { VideoFeed, VideoFeedType } from '../../types/video-feed-types';
import { VideoToSpeakerDetails } from '../../types/video-speaker-details';
import { getActiveSpeakersWithinTimeRange } from '../../utils/activeSpeaker';
import {
  doesClipHaveScenes,
  isClipAudiogram,
  isClipFromRecordingStudio,
  isClipLayoutDefault,
  isLayoutWithVisionDataEnabledPure
} from '../../utils/clipUtils';
import { isLayoutDefault, isLayoutAudiogram, isLayoutGrid, isLayoutPip } from '../../utils/layout';
import { isFallingBetweenRange } from '../../utils/timeUtils';
import { getSpeakerLabelConfig } from './getSpeakerInfo';

export const buildSpeakerDetailsMap = (videoFeeds: VideoFeed[]): Record<string, SpeakerWithDetails> => {
  const map: Record<string, SpeakerWithDetails> = {};
  for (const feed of videoFeeds) {
    if (feed.speakerDetails && feed.speakerKey) {
      map[feed.speakerKey] = feed.speakerDetails;
    }
  }
  return map;
};
export const buildVideoToSpeakerDetails = (videoFeeds: VideoFeed[]): VideoToSpeakerDetails => {
  const map: VideoToSpeakerDetails = {};
  for (const feed of videoFeeds) {
    if (feed.type === VideoFeedType.ORIGINAL) {
      continue;
    }

    let videoType: RecordingVideoType;
    switch (feed.type) {
      case VideoFeedType.SCREENSHARE:
        videoType = RecordingVideoType.SCREENSHARE;
        break;
      case VideoFeedType.SHARED_VIDEO:
        videoType = RecordingVideoType.SHARED_VIDEO;
        break;
      default:
        videoType = RecordingVideoType.USER_FEED;
        break;
    }

    map[feed.id] = {
      speakerKey: feed.speakerKey,
      videoType
    };
  }
  return map;
};
export const getVideoFeedSpeakerKey = (videoFeeds: VideoFeed[], videoId: string): string | null => {
  const feed = videoFeeds.find(f => f.id === videoId);
  return feed?.speakerKey || null;
};

export const isVideoFeedScreenshare = (videoFeeds: VideoFeed[], videoId: string): boolean => {
  const feed = videoFeeds.find(f => f.id === videoId);
  return feed?.type === VideoFeedType.SCREENSHARE;
};

export const doesVideoFeedHaveSpeaker = (videoFeeds: VideoFeed[], videoId: string): boolean => {
  const feed = videoFeeds.find(f => f.id === videoId);
  if (!feed) {
    return false;
  }
  return !!feed.speakerKey;
};
export const getVideosAndTimeRanges = ({
  videoFeeds
}: {
  videoFeeds: VideoFeed[];
}): {
  videoId: string;
  ranges: { start: number; end: number }[];
  isScreenshare: boolean;
  isOriginalVideo: boolean;
}[] => {
  // Legacy parity: overlaps are computed with screenshare streams first.
  const orderedFeeds = [
    ...videoFeeds.filter(feed => feed.type === VideoFeedType.SCREENSHARE),
    ...videoFeeds.filter(feed => feed.type !== VideoFeedType.SCREENSHARE && feed.type !== VideoFeedType.ORIGINAL),
    ...videoFeeds.filter(feed => feed.type === VideoFeedType.ORIGINAL)
  ];

  return orderedFeeds.map(feed => ({
    videoId: feed.id,
    ranges: feed.stageTimeSpans.map(span => ({ start: span.start, end: span.end })),
    isScreenshare: feed.type === VideoFeedType.SCREENSHARE,
    isOriginalVideo: feed.type === VideoFeedType.ORIGINAL
  }));
};

// --- Speaker Overlaps from Video Feeds ---
export interface SpeakerOverlap {
  fromFrame: number;
  toFrame: number;
  videoIds: string[];
  hasScreenshare: boolean;
  isOriginalVideo: boolean;
}
export interface FramePointWithLayout {
  frame: number;
  layout: LayoutType;
}
export interface ProcessedFramePoints {
  sortedFramePoints: FramePointWithLayout[];
  speakerOverlaps: SpeakerOverlap[];
}

export const addFramePointFromSceneOrFallback = (
  time: number,
  fallbackLayout: LayoutType,
  scenes: Scenes,
  fps: number,
  framePointsWithLayout: FramePointWithLayout[]
): FramePointWithLayout[] => {
  if (scenes.length > 0) {
    const scene = scenes.find(scene => scene.fromFrame / fps <= time && time < scene.toFrame / fps);
    if (scene) {
      return [...framePointsWithLayout, { frame: time, layout: scene.details.layout }];
    }
  }
  return [...framePointsWithLayout, { frame: time, layout: fallbackLayout }];
};

export const addFramePointsForActiveSpeakers = (
  fromFrame: number,
  toFrame: number,
  speakerDetails: Record<string, SpeakerWithDetails>,
  layout: LayoutType,
  framePointsWithLayout: FramePointWithLayout[]
): FramePointWithLayout[] => {
  const activeSpeakers = getActiveSpeakersWithinTimeRange(fromFrame, toFrame, speakerDetails);
  const newFramePoints = activeSpeakers
    .filter((speaker): speaker is NonNullable<typeof speaker> => speaker !== null)
    .flatMap(speaker => [
      { frame: speaker.start, layout },
      { frame: speaker.end, layout }
    ]);
  return [...framePointsWithLayout, ...newFramePoints];
};
export const addFramePointsForTimeRange = (
  fromFrame: number,
  toFrame: number,
  layout: LayoutType,
  framePointsWithLayout: FramePointWithLayout[]
): FramePointWithLayout[] => {
  return [...framePointsWithLayout, { frame: fromFrame, layout }, { frame: toFrame, layout }];
};
export const sortAndDeduplicateFramePoints = (framePoints: FramePointWithLayout[]): FramePointWithLayout[] => {
  const sortedFrames = framePoints.sort((a, b) => a.frame - b.frame);
  return sortedFrames.reduce((acc, curr, index) => {
    if (index === sortedFrames.length - 1) {
      acc.push(curr);
    } else if (curr.frame !== sortedFrames[index + 1].frame) {
      acc.push(curr);
    }
    return acc;
  }, [] as FramePointWithLayout[]);
};
export const getVisibleVideosInTimeRange = (
  fromFrame: number,
  toFrame: number,
  videosAndTimeRanges: ReturnType<typeof getVideosAndTimeRanges>,
  scenes: Scenes,
  clip: Clip,
  fps: number
): typeof videosAndTimeRanges => {
  return videosAndTimeRanges.filter(video => {
    if (!video.ranges.length) {
      return false;
    }

    if (scenes.length > 0) {
      const scene = scenes.find(scene => fromFrame >= scene.fromFrame / fps && toFrame <= scene.toFrame / fps);
      if (scene && !scene.details.visible_video_ids.includes(video.videoId)) {
        return false;
      }
    } else if (
      clip.asset_metadata.layout !== 'SPEAKER' &&
      clip.asset_metadata.visible_video_ids &&
      !clip.asset_metadata.visible_video_ids.includes(video.videoId)
    ) {
      return false;
    }

    return video.ranges.some(({ start, end }) => start <= fromFrame && end >= toFrame);
  });
};
export const filterSpeakerVideos = ({
  allPresentSpeakers,
  isFirstScreenshare
}: {
  allPresentSpeakers: ReturnType<typeof getVisibleVideosInTimeRange>;
  isFirstScreenshare: boolean;
}) => {
  return allPresentSpeakers.filter((_, index) => (isFirstScreenshare ? index < 9 : index < 8));
};
export const reorderSpeakersByActiveSpeaker = ({
  videoFeeds,
  allPresentSpeakers,
  activeSpeakers,
  layout
}: {
  videoFeeds: VideoFeed[];
  allPresentSpeakers: ReturnType<typeof getVideosAndTimeRanges>;
  activeSpeakers: ReturnType<typeof getActiveSpeakersWithinTimeRange>;
  layout: LayoutType;
}): void => {
  const getSpeakerKeyForVideoId = (videoId: string): string | null => getVideoFeedSpeakerKey(videoFeeds, videoId);

  if (layout === 'PIP') {
    if (activeSpeakers.length >= 2) {
      const firstActiveSpeaker = activeSpeakers[0];
      const secondActiveSpeaker = activeSpeakers[1];

      const speakerIdsOfAllPresent = allPresentSpeakers.map(s => getSpeakerKeyForVideoId(s.videoId));

      const firstSpeakerIndex = speakerIdsOfAllPresent.indexOf(firstActiveSpeaker.speakerId);
      if (firstSpeakerIndex !== -1) {
        const firstSpeaker = allPresentSpeakers.splice(firstSpeakerIndex, 1)[0];
        allPresentSpeakers.unshift(firstSpeaker);
      }

      const secondSpeakerIndex = speakerIdsOfAllPresent.indexOf(secondActiveSpeaker.speakerId);
      if (secondSpeakerIndex !== -1 && secondSpeakerIndex > 0) {
        const secondSpeaker = allPresentSpeakers.splice(secondSpeakerIndex, 1)[0];
        allPresentSpeakers.splice(1, 0, secondSpeaker);
      }
    } else if (activeSpeakers.length === 1) {
      const currentActiveSpeaker = activeSpeakers[0];
      const speakerIdsOfAllPresent = allPresentSpeakers.map(s => getSpeakerKeyForVideoId(s.videoId));
      const activeSpeakerIndex = speakerIdsOfAllPresent.indexOf(currentActiveSpeaker.speakerId);

      if (activeSpeakerIndex !== -1) {
        const activeSpeaker = allPresentSpeakers.splice(activeSpeakerIndex, 1)[0];
        allPresentSpeakers.unshift(activeSpeaker);
      }
    }
  } else {
    const currentActiveSpeaker = activeSpeakers[0];
    if (currentActiveSpeaker) {
      const speakerIdsOfAllPresent = allPresentSpeakers.map(s => getSpeakerKeyForVideoId(s.videoId));
      allPresentSpeakers.unshift(
        allPresentSpeakers.splice(speakerIdsOfAllPresent.indexOf(currentActiveSpeaker.speakerId), 1)[0]
      );
    }
  }
};
export const processFramePoints = (
  framePoints: FramePointWithLayout[],
  clip: Clip,
  fps: number,
  videosAndTimeRanges: ReturnType<typeof getVideosAndTimeRanges>,
  scenes: Scenes,
  speakerDetails: Record<string, SpeakerWithDetails>,
  videoFeeds: VideoFeed[]
): ProcessedFramePoints => {
  let finalFramePoints = framePoints;

  if (!isClipFromRecordingStudio(clip)) {
    const defaultLayoutScenes = scenes.filter(scene => isLayoutDefault(scene.details.layout));
    const defaultSceneTimespans = defaultLayoutScenes.map(scene => ({
      start: scene.fromFrame / fps,
      end: scene.toFrame / fps
    }));
    finalFramePoints = framePoints.filter(fp => !isFallingBetweenRange(defaultSceneTimespans, fp.frame));
  }

  const sortedFramePoints = sortAndDeduplicateFramePoints(finalFramePoints);
  const speakerOverlaps: SpeakerOverlap[] = [];

  const individualVideos = videosAndTimeRanges.filter(v => !v.isOriginalVideo);
  const originalVideos = videosAndTimeRanges.filter(v => v.isOriginalVideo);

  for (let i = 0; i < sortedFramePoints.length - 1; i++) {
    const fromFrame = sortedFramePoints[i].frame;
    const toFrame =
      i === sortedFramePoints.length - 1 ? clip.asset_metadata.duration || 0 : sortedFramePoints[i + 1].frame;
    const currentLayout = sortedFramePoints[i].layout;

    if (isLayoutDefault(currentLayout)) {
      speakerOverlaps.push({
        fromFrame: fromFrame * fps,
        toFrame: toFrame * fps,
        videoIds: originalVideos.map(v => v.videoId),
        hasScreenshare: false,
        isOriginalVideo: true
      });
      continue;
    }

    const activeSpeakers = getActiveSpeakersWithinTimeRange(fromFrame, toFrame, speakerDetails);
    const allPresentSpeakers = getVisibleVideosInTimeRange(fromFrame, toFrame, individualVideos, scenes, clip, fps);
    const isFirstScreenshare = !!allPresentSpeakers[0]?.isScreenshare;

    if (
      allPresentSpeakers.length > 1 &&
      (isLayoutAudiogram(currentLayout) ||
        ((!isLayoutGrid(currentLayout) || isLayoutPip(currentLayout)) && !isFirstScreenshare))
    ) {
      reorderSpeakersByActiveSpeaker({
        allPresentSpeakers,
        activeSpeakers,
        layout: currentLayout,
        videoFeeds
      });
    }

    speakerOverlaps.push({
      fromFrame: fromFrame * fps,
      toFrame: toFrame * fps,
      videoIds: filterSpeakerVideos({ allPresentSpeakers, isFirstScreenshare }).map(s => s.videoId),
      hasScreenshare: isFirstScreenshare,
      isOriginalVideo: false
    });
  }

  return { sortedFramePoints, speakerOverlaps };
};
export const getSpeakerOverlaps = ({
  videoFeeds,
  clip,
  fps,
  scenes,
  speakerAnalysis,
  isFullFaceRecEnabled,
  isFullFaceRecForPreRecordedEnabled
}: {
  videoFeeds: VideoFeed[];
  clip: Clip;
  fps: number;
  scenes: Scenes;
  speakerAnalysis?: SpeakersAnalysis;
  isFullFaceRecEnabled: boolean;
  isFullFaceRecForPreRecordedEnabled: boolean;
}): SpeakerOverlap[] => {
  const speakerDetails = buildSpeakerDetailsMap(videoFeeds);
  const videosAndTimeRanges = getVideosAndTimeRanges({ videoFeeds });

  let framePointsWithLayout: FramePointWithLayout[] = [];

  videosAndTimeRanges.forEach(video => {
    video.ranges.forEach(range => {
      framePointsWithLayout = addFramePointFromSceneOrFallback(
        range.start,
        clip.asset_metadata.layout,
        scenes,
        fps,
        framePointsWithLayout
      );
      framePointsWithLayout = addFramePointFromSceneOrFallback(
        range.end,
        clip.asset_metadata.layout,
        scenes,
        fps,
        framePointsWithLayout
      );
    });
  });

  if (
    isLayoutWithVisionDataEnabledPure({ clip, isFullFaceRecEnabled, isFullFaceRecForPreRecordedEnabled }) &&
    speakerAnalysis?.times &&
    speakerAnalysis.times.length > 0
  ) {
    speakerAnalysis.times.forEach(time => {
      framePointsWithLayout = addFramePointFromSceneOrFallback(
        time,
        clip.asset_metadata.layout,
        scenes,
        fps,
        framePointsWithLayout
      );
    });
  }

  // TEMPORARY parity guard: ensure latest clip-end boundary is present so the
  // video-feeds config does not miss the final overlap window while legacy
  // config generation is still used for comparison. Remove this once the old
  // config generation path is removed.
  framePointsWithLayout = addFramePointFromSceneOrFallback(
    clip.asset_metadata.end,
    clip.asset_metadata.layout,
    scenes,
    fps,
    framePointsWithLayout
  );

  if (scenes.length > 0) {
    scenes.forEach(scene => {
      framePointsWithLayout = addFramePointsForTimeRange(
        scene.fromFrame / fps,
        scene.toFrame / fps,
        scene.details.layout,
        framePointsWithLayout
      );

      if (
        scene.details.layout === 'SPEAKER' ||
        scene.details.layout === 'AUDIOGRAM' ||
        scene.details.layout === 'FOCUS' ||
        scene.details.layout === 'PIP'
      ) {
        framePointsWithLayout = addFramePointsForActiveSpeakers(
          scene.fromFrame / fps,
          scene.toFrame / fps,
          speakerDetails,
          scene.details.layout,
          framePointsWithLayout
        );
      }
    });
  } else if (clip.asset_metadata.layout !== 'GRID') {
    const sortedFrames = framePointsWithLayout.map(fp => fp.frame).sort((a, b) => a - b);
    for (let i = 0; i < sortedFrames.length - 1; i++) {
      const fromFrame = sortedFrames[i];
      const toFrame = i === sortedFrames.length - 1 ? clip.asset_metadata.duration || 0 : sortedFrames[i + 1];

      framePointsWithLayout = addFramePointsForActiveSpeakers(
        fromFrame,
        toFrame,
        speakerDetails,
        clip.asset_metadata.layout,
        framePointsWithLayout
      );
    }
  }

  const { speakerOverlaps } = processFramePoints(
    framePointsWithLayout,
    clip,
    fps,
    videosAndTimeRanges,
    scenes,
    speakerDetails,
    videoFeeds
  );

  return speakerOverlaps;
};
export const removeScreenshareWithoutSpeakerVF = ({
  videoFeeds,
  videoIds
}: {
  videoFeeds: VideoFeed[];
  videoIds: string[];
}): string[] => {
  return videoIds.filter(recordingId => {
    if (!isVideoFeedScreenshare(videoFeeds, recordingId)) {
      return true;
    }
    return doesVideoFeedHaveSpeaker(videoFeeds, recordingId);
  });
};
export const removeScreenshareFromVideoIdsVF = ({
  videoFeeds,
  videoIds
}: {
  videoFeeds: VideoFeed[];
  videoIds: string[];
}): string[] => {
  return videoIds.filter(id => !isVideoFeedScreenshare(videoFeeds, id));
};
export const shouldShowSpeakerLabels = ({ videoFeed, clip }: { videoFeed: VideoFeed; clip: Clip }): boolean => {
  if (isClipLayoutDefault(clip) && !doesClipHaveScenes(clip)) {
    return false;
  }
  if (!videoFeed.speakerKey) {
    return false;
  }
  if (isClipAudiogram(clip)) {
    return true;
  }
  if (videoFeed.type === VideoFeedType.SCREENSHARE) {
    return false;
  }
  return !!clip.asset_metadata.magicLayout?.showSpeakerLabels;
};
const audiogramContainerStyle = { visibility: 'hidden', width: '1px', height: '1px' };

export const getVideoElements = ({
  videoFeeds,
  clip,
  fromFrame,
  toFrame,
  isPlatformBrandKitEnabled,
  clipBrandKitStore,
  brandKitStore,
  platformBrandKit,
  organization
}: {
  videoFeeds: VideoFeed[];
  clip: Clip;
  fromFrame: number;
  toFrame: number;
  isPlatformBrandKitEnabled: boolean;
  clipBrandKitStore: BrandKitStore;
  brandKitStore: BrandKitStore;
  platformBrandKit: PlatformBrandKitStore;
  organization?: string | null | undefined;
}): VideoElement[] => {
  return videoFeeds.map((feed, index) => {
    const isOriginal = feed.type === VideoFeedType.ORIGINAL;
    const isScreenshare = feed.type === VideoFeedType.SCREENSHARE;

    let speakerLabel: SpeakerLabel | undefined;
    let speakerDetails: SpeakerWithDetails | undefined;

    const shouldGetSpeakerInfo = !isOriginal && shouldShowSpeakerLabels({ videoFeed: feed, clip });

    if (shouldGetSpeakerInfo && feed.speakerDetails) {
      const labelConfig = getSpeakerLabelConfig({
        videoFeed: feed,
        clip,
        clipBrandKitStore,
        brandKitStore,
        platformBrandKit,
        organization,
        isPlatformBrandKitEnabled
      });
      speakerLabel = labelConfig.speakerLabel;
      speakerDetails = labelConfig.speakerDetails ?? undefined;
    }

    const containerStyle = isClipAudiogram(clip) && !isOriginal ? audiogramContainerStyle : {};

    const videoName = isOriginal
      ? 'Original Video'
      : isScreenshare && feed.id === SCREENSHARE_VIDEO_ID
        ? 'upload_screenshare'
        : feed.source.altText || `Video ${feed.id}${index}`;

    return {
      id: feed.id,
      name: videoName,
      type: ElementType.VIDEO,
      fromFrame,
      toFrame,
      details: {
        src: feed.source.src,
        preview: feed.source.preview,
        altText: isOriginal ? 'Original Video' : feed.source.altText,
        volume: [{ value: 100, fromFrame, toFrame }]
      },
      trim: [],
      style: {},
      containerStyle,
      speakerLabel,
      speakerDetails,
      isOriginalVideo: isOriginal,
      ...(feed.aspectRatio !== undefined && { aspectRatio: feed.aspectRatio })
    };
  });
};
